// Decides which files in a .zip are unpacked into their own uploads. No runtime dependencies, so the app can apply the same rules before upload.
import {
  getFileExtension,
  isBlockedFileExtension,
} from "../../util/mime-types";
import { hasControlCharacters } from "../sanitize";
import {
  MAX_CHAT_UPLOAD_FILES,
  MAX_CHAT_UPLOAD_SIZE_BYTES,
  MAX_CHAT_UPLOAD_TOTAL_BYTES,
} from "../upload-limits";

const MiB = 1024 * 1024;

/** Limits on unpacking the zips attached to one message. */
export type ZipUnpackLimits = {
  /** Compressed size of the archive. It is read by byte range, never held in memory. */
  maxArchiveBytes: number;
  /** Central-directory entries, junk and folders included. */
  maxEntries: number;
  /** Files on one message: files attached directly, other than archives, plus every file unpacked. */
  maxMembers: number;
  /** Bytes on one message: files attached directly, other than archives, plus every file unpacked. */
  maxTotalBytes: number;
  /** A file whose uncompressed-to-compressed ratio exceeds this is left out. */
  maxCompressionRatio: number;
  /** Small, highly repetitive files (SVG, text) compress far past the ratio without being bombs. */
  ratioCheckMinBytes: number;
};

export const ZIP_UNPACK_LIMITS: ZipUnpackLimits = {
  maxArchiveBytes: MAX_CHAT_UPLOAD_SIZE_BYTES,
  maxEntries: 2_000,
  maxMembers: MAX_CHAT_UPLOAD_FILES,
  // The same budget as the files a user attaches directly to one message.
  maxTotalBytes: MAX_CHAT_UPLOAD_TOTAL_BYTES,
  maxCompressionRatio: 100,
  ratioCheckMinBytes: MiB,
};

/** What is left of the message's file and byte limits for unpacked files. */
export type ZipUnpackBudget = { files: number; bytes: number };

/** One central-directory entry, in a form any zip reader can produce. */
export type ZipEntryInfo = {
  /** Path inside the archive, with forward slashes. A folder ends with "/". */
  path: string;
  compressedSize: number;
  uncompressedSize: number;
  /** The entry's compression method code: 0 is stored and 8 is deflated. */
  compressionMethod: number;
  encrypted: boolean;
  symbolicLink: boolean;
};

/** A file left out of the unpacked list, with the reason. */
export type SkippedZipEntry = { path: string; reason: string };

/** A file that is unpacked: its position in the entry list, its path, and its file name. */
export type PlannedZipEntry = { index: number; path: string; baseName: string };

/** Why an archive stays a single file, as a code the app turns into its own copy. */
export type ZipKeptReason =
  | { code: "archive_too_large"; maxBytes: number }
  | { code: "too_many_entries"; maxEntries: number }
  | { code: "too_many_files"; files: number; available: number }
  | { code: "too_many_bytes"; bytes: number; available: number };

/** Which files of an archive are unpacked, or why the archive stays a single file. */
export type ZipUnpackPlan =
  | {
      outcome: "unpack";
      planned: PlannedZipEntry[];
      skipped: SkippedZipEntry[];
      /** Uncompressed bytes of the planned files. */
      bytes: number;
    }
  | { outcome: "keep"; reason: ZipKeptReason };

const STORED = 0;
const DEFLATED = 8;

const JUNK_FILE_NAMES = new Set([".ds_store", "thumbs.db", "desktop.ini"]);

/** Whether a file name is a .zip that is unpacked. Office documents, IDML and EPUB are zips too, but their own extractors read them. */
export function isZipFileName(fileName: string): boolean {
  return getFileExtension(fileName) === ".zip";
}

/** Why an archive of this size and entry count stays a single file, or null when both are within the limits. */
export function archiveLimitReason(
  { sizeBytes, entryCount }: { sizeBytes: number; entryCount: number },
  limits: ZipUnpackLimits,
): ZipKeptReason | null {
  if (sizeBytes > limits.maxArchiveBytes) {
    return { code: "archive_too_large", maxBytes: limits.maxArchiveBytes };
  }
  if (entryCount > limits.maxEntries) {
    return { code: "too_many_entries", maxEntries: limits.maxEntries };
  }
  return null;
}

/** The reason in words for logs and the agent, which passes it on to the user. */
export function describeZipKeptReason(reason: ZipKeptReason): string {
  switch (reason.code) {
    case "archive_too_large":
      return `archive is over ${reason.maxBytes} bytes`;
    case "too_many_entries":
      return `archive has more than ${reason.maxEntries} entries`;
    case "too_many_files":
      return `it holds ${reason.files} files and only ${reason.available} more fit on this message`;
    case "too_many_bytes":
      return `it expands to ${reason.bytes} bytes and only ${reason.available} more fit on this message`;
  }
}

/** Plans which entries are unpacked, within what is left of the message's budget. */
export function planZipUnpack(
  entries: ZipEntryInfo[],
  { limits, budget }: { limits: ZipUnpackLimits; budget: ZipUnpackBudget },
): ZipUnpackPlan {
  const planned: PlannedZipEntry[] = [];
  const skipped: SkippedZipEntry[] = [];
  let bytes = 0;

  for (const [index, entry] of entries.entries()) {
    const { path } = entry;
    if (path.endsWith("/")) continue;

    const segments = path.split("/");
    const baseName = segments[segments.length - 1] ?? "";
    if (
      segments.includes("__MACOSX") ||
      baseName.startsWith("._") ||
      JUNK_FILE_NAMES.has(baseName.toLowerCase())
    ) {
      continue;
    }

    const skip = skipReason(entry, baseName, limits);
    if (skip) {
      skipped.push({ path, reason: skip });
      continue;
    }

    bytes += entry.uncompressedSize;
    planned.push({ index, path, baseName });
  }

  // Counted to the end so the reason gives the archive's real size, which the agent passes on to the user.
  if (planned.length > budget.files) {
    return {
      outcome: "keep",
      reason: {
        code: "too_many_files",
        files: planned.length,
        available: Math.max(budget.files, 0),
      },
    };
  }
  if (bytes > budget.bytes) {
    return {
      outcome: "keep",
      reason: {
        code: "too_many_bytes",
        bytes,
        available: Math.max(budget.bytes, 0),
      },
    };
  }
  return { outcome: "unpack", planned, skipped, bytes };
}

function skipReason(
  entry: ZipEntryInfo,
  baseName: string,
  limits: ZipUnpackLimits,
): string | null {
  if (entry.symbolicLink) return "symbolic link";
  if (entry.encrypted) return "encrypted";
  if (
    entry.compressionMethod !== STORED &&
    entry.compressionMethod !== DEFLATED
  ) {
    return "unsupported compression method";
  }
  // The path, not only the name, is shown to the agent, so a control character in any folder name skips the file.
  if (!baseName.trim() || hasControlCharacters(entry.path)) {
    return "unusable file name";
  }
  if (entry.uncompressedSize === 0) return "empty file";
  if (isZipFileName(baseName)) return "nested archive";
  if (isBlockedFileExtension(baseName)) return "blocked file type";
  if (
    entry.uncompressedSize >= limits.ratioCheckMinBytes &&
    entry.uncompressedSize / Math.max(entry.compressedSize, 1) >
      limits.maxCompressionRatio
  ) {
    return "unsafe compression ratio";
  }
  return null;
}
